import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { AlarmClock, Archive, ArchiveRestore, AtSign, Bell, CheckCheck, MoreHorizontal } from 'lucide-react';
import type { NotificationInboxView, NotificationView } from '@vwt/contracts';
import { Button, cn, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, EmptyState, Tabs, TabsList, TabsTrigger, toast } from '@vwt/ui';
import { api, POLL_ACTIVE, queryClient, useApiMutation } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { formatDateTime } from '../../lib/format.js';
import { PageHeader, QueryState, TimeAgo } from '../../components/common.js';

const VIEWS: NotificationInboxView[] = ['inbox', 'mentions', 'unread', 'snoozed', 'archived'];

const later = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
};

/** Own in-app notifications: views, read state, snooze and archive (preferences stay in Settings). */
export function InboxPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = (VIEWS as string[]).includes(params.get('view') ?? '') ? (params.get('view') as NotificationInboxView) : 'inbox';
  const list = useInfiniteQuery({
    queryKey: ['listNotifications', 'inbox', view],
    queryFn: ({ pageParam, signal }) => api.call('listNotifications', { query: { view, limit: '50', ...(pageParam ? { cursor: pageParam } : {}) }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last?.meta.nextCursor ?? undefined,
    refetchInterval: POLL_ACTIVE,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p?.data ?? []) ?? [], [list.data]);
  const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: ['listNotifications'] }), queryClient.invalidateQueries({ queryKey: ['countUnreadNotifications'] })]);
  const markRead = useApiMutation('markNotificationsRead');
  const update = useApiMutation('updateNotifications');
  const run = async (fn: () => Promise<unknown>, message?: string) => {
    try {
      await fn();
      await refresh();
      if (message) toast.success(message);
    } catch (error) {
      toastError(error);
    }
  };
  const open = (n: NotificationView) => {
    if (!n.readAt) void markRead.mutateAsync({ body: { ids: [n.id] } }).then(refresh);
    if (n.link) void navigate(n.link);
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('inbox.title')}
        subtitle={t('inbox.subtitle')}
        actions={
          <>
            <Button variant="secondary" onClick={() => void run(() => markRead.mutateAsync({ body: { all: true } }), t('inbox.allRead'))}>
              <CheckCheck /> {t('inbox.markAllRead')}
            </Button>
            <Button variant="ghost" onClick={() => void run(() => update.mutateAsync({ body: { action: 'archive', allRead: true } }), t('inbox.archivedRead'))}>
              <Archive /> {t('inbox.archiveRead')}
            </Button>
          </>
        }
      >
        <Tabs value={view} onValueChange={(v) => setParams(v === 'inbox' ? {} : { view: v })}>
          <TabsList className="border-b-0">
            {VIEWS.map((v) => (
              <TabsTrigger key={v} value={v}>
                {v === 'mentions' ? <AtSign /> : v === 'snoozed' ? <AlarmClock /> : v === 'archived' ? <Archive /> : null}
                {t(`inbox.tabs.${v}`)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </PageHeader>
      <div className="min-h-0 flex-1 overflow-auto">
        <QueryState query={list} empty={items.length === 0 ? <EmptyState icon={<Bell />} title={t(`inbox.empty.${view}`)} /> : undefined}>
          <ul className="mx-auto flex max-w-4xl flex-col divide-y divide-border">
            {items.map((n) => (
              <li key={n.id} className={cn('flex items-start gap-3 px-4 py-3', !n.readAt && 'bg-accent-soft/20')}>
                <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-accent')} aria-label={n.readAt ? undefined : t('inbox.unread')} />
                <button type="button" onClick={() => open(n)} className="min-w-0 flex-1 text-left">
                  <span className="block text-xs text-fg-subtle">
                    {t(`notifications.events.${n.event}`)} · <TimeAgo iso={n.createdAt} />
                    {n.snoozedUntil ? ` · ${t('inbox.snoozedUntil', { time: formatDateTime(n.snoozedUntil) })}` : ''}
                  </span>
                  <span className={cn('block truncate text-sm', n.readAt ? 'text-fg-muted' : 'font-medium text-fg')}>{n.title}</span>
                  {n.body ? <span className="block truncate text-xs text-fg-subtle">{n.body}</span> : null}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="iconSm" aria-label={t('inbox.actions')}>
                      <MoreHorizontal />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!n.readAt ? <DropdownMenuItem onSelect={() => void run(() => markRead.mutateAsync({ body: { ids: [n.id] } }))}>{t('inbox.markRead')}</DropdownMenuItem> : null}
                    {view === 'archived' ? (
                      <DropdownMenuItem onSelect={() => void run(() => update.mutateAsync({ body: { action: 'unarchive', ids: [n.id] } }))}>
                        <ArchiveRestore /> {t('inbox.unarchive')}
                      </DropdownMenuItem>
                    ) : (
                      <>
                        <DropdownMenuItem onSelect={() => void run(() => update.mutateAsync({ body: { action: 'archive', ids: [n.id] } }))}>
                          <Archive /> {t('inbox.archive')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel>{t('inbox.snooze')}</DropdownMenuLabel>
                        {(
                          [
                            [1, 'inbox.snoozeFor.day'],
                            [3, 'inbox.snoozeFor.threeDays'],
                            [7, 'inbox.snoozeFor.week'],
                          ] as const
                        ).map(([days, label]) => (
                          <DropdownMenuItem key={days} onSelect={() => void run(() => update.mutateAsync({ body: { action: 'snooze', ids: [n.id], until: later(days) } }), t('inbox.snoozed'))}>
                            <AlarmClock /> {t(label)}
                          </DropdownMenuItem>
                        ))}
                        {n.snoozedUntil ? <DropdownMenuItem onSelect={() => void run(() => update.mutateAsync({ body: { action: 'unsnooze', ids: [n.id] } }))}>{t('inbox.unsnooze')}</DropdownMenuItem> : null}
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            ))}
          </ul>
          {list.hasNextPage ? (
            <div className="flex justify-center p-3">
              <Button variant="secondary" size="sm" onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
                {t('common.loadMore')}
              </Button>
            </div>
          ) : null}
        </QueryState>
      </div>
    </div>
  );
}
